import React from "react";
import { Handle, Position, NodeProps, Node } from "@xyflow/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  MessageSquare,
  HelpCircle,
  GitBranch,
  Wrench,
  PhoneForwarded,
  Tag,
  PhoneOff,
  Hand,
  X,
} from "lucide-react";
import type { AiNodeActionType, AiConversationNodeData } from "@/model/interface/AiConversationNode";

export type AiConversationNodeType = Node<AiConversationNodeData>;

const nodeTypeConfig: Record<AiNodeActionType, { icon: React.ElementType; label: string; color: string }> = {
  GREETING: { icon: Hand, label: "Greeting", color: "bg-emerald-500" },
  SAY_MESSAGE: { icon: MessageSquare, label: "Say Message", color: "bg-blue-500" },
  ASK_QUESTION: { icon: HelpCircle, label: "Ask Question", color: "bg-amber-500" },
  CONDITION: { icon: GitBranch, label: "Condition", color: "bg-purple-500" },
  TOOL_CALL: { icon: Wrench, label: "Tool Call", color: "bg-orange-500" },
  TRANSFER: { icon: PhoneForwarded, label: "Transfer", color: "bg-cyan-500" },
  SET_OUTCOME: { icon: Tag, label: "Set Outcome", color: "bg-pink-500" },
  END_CONVERSATION: { icon: PhoneOff, label: "End Conversation", color: "bg-red-500" },
};

export default function AiConversationNode({ data, selected }: NodeProps<AiConversationNodeType>) {
  const config = nodeTypeConfig[data.actionType];
  const Icon = config?.icon || MessageSquare;

  return (
    <Card className={`min-w-[250px] ${selected ? "ring-2 ring-primary" : ""}`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3" />

      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${config?.color} text-white`}>
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <div className="font-semibold text-sm">{config?.label}</div>
              <div className="text-xs text-muted-foreground">{data.label}</div>
            </div>
          </div>

          {data.onDelete && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={(event) => {
                event.stopPropagation();
                data.onDelete?.();
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        <div className="space-y-1 text-xs text-muted-foreground">
          {data.actionType === "GREETING" && data.message && (
            <div className="truncate">"{data.message}"</div>
          )}

          {data.actionType === "SAY_MESSAGE" && data.message && (
            <div className="truncate">"{data.message}"</div>
          )}

          {data.actionType === "ASK_QUESTION" && data.questionPrompt && (
            <div className="truncate">Ask: "{data.questionPrompt}"</div>
          )}

          {data.actionType === "ASK_QUESTION" && data.variableName && (
            <div>Store in: {data.variableName}</div>
          )}

          {data.actionType === "CONDITION" && data.conditionField && (
            <div>If {data.conditionField} {data.conditionOperator} {data.conditionValue}</div>
          )}

          {data.actionType === "TOOL_CALL" && data.toolName && (
            <div>Tool: {data.toolName}</div>
          )}

          {data.actionType === "TRANSFER" && data.transferTarget && (
            <div>To: {data.transferTarget}</div>
          )}

          {data.actionType === "SET_OUTCOME" && data.outcomeName && (
            <div>Outcome: {data.outcomeName}</div>
          )}
        </div>

        {data.onEdit && (
          <Button
            variant="outline"
            size="sm"
            className="w-full h-7 text-xs"
            onClick={(event) => {
              event.stopPropagation();
              data.onEdit?.();
            }}
          >
            Edit Details
          </Button>
        )}
      </div>

      {data.actionType !== "END_CONVERSATION" && (
        <Handle type="source" position={Position.Bottom} className="w-3 h-3" />
      )}

      {data.actionType === "CONDITION" && (
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="true"
            className="w-3 h-3"
            style={{ left: "30%" }}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="false"
            className="w-3 h-3"
            style={{ left: "70%" }}
          />
        </>
      )}
    </Card>
  );
}
